import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { apiClient, handleApiError } from '@/infrastructure/api/client'
import { resolveLanguage } from '@/application/i18n/language'
import type {
  Lead,
  LeadFilters,
  CreateLeadFormData,
  UpdateLeadFormData,
  LeadGroup,
} from '@/domains/types'
import {
  useGetAllInvestigations,
  investigationKeys,
} from '@/application/services/investigation.service'

const LEAD_API_BASE = '/leads'

export const leadKeys = {
  all: ['leads'] as const,
  // Locale-scoped for the same reason as investigationKeys.byId/read — see
  // that file (TRANSLATION-PLAN.md §9 Phase 1).
  byId: (entityId: string, locale: string) => ['leads', entityId, locale] as const,
  // No-locale prefixes: what mutations invalidate, so every cached locale
  // variant is dropped together.
  byIdPrefix: (entityId: string) => ['leads', entityId] as const,
  byInvestigation: (investigationId: string, locale: string) =>
    ['leads', 'by-investigation', investigationId, locale] as const,
  byInvestigationPrefix: (investigationId: string) =>
    ['leads', 'by-investigation', investigationId] as const,
  bySubmitter: (username: string) => ['leads', 'by-submitter', username] as const,
  search: (filters: LeadFilters) => ['leads', 'search', filters] as const,
  grouped: () => ['leads', 'grouped'] as const,
  myLeads: () => ['leads', 'my-leads'] as const,
  userLeads: (username: string) => ['leads', 'user', username] as const,
}

export function useGetAllLeads() {
  return useQuery({
    queryKey: leadKeys.all,
    queryFn: async (): Promise<Lead[]> => {
      const response = await apiClient.get<Lead[]>(LEAD_API_BASE)
      return response.data
    },
    staleTime: 1000 * 60 * 1,
  })
}

export function useGetLeadById(entityId: string) {
  const { i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  return useQuery({
    queryKey: leadKeys.byId(entityId, locale),
    queryFn: async (): Promise<Lead | null> => {
      const response = await apiClient.get<Lead>(`${LEAD_API_BASE}/${entityId}`, {
        params: { lang: locale },
      })
      return response.data
    },
    enabled: !!entityId,
  })
}

/**
 * Raw, locale-independent read of a lead — no `?lang=` param, so the backend
 * never runs the translation fallback chain (TRANSLATION-PLAN.md §6) and
 * always returns the true source-language `title`/`description`.
 *
 * Every other lead read (`useGetLeadById`, `useGetLeadsByInvestigation`, …)
 * passes the admin's own UI locale as `lang`, which is correct for display
 * but wrong for editing: when the admin's UI locale differs from the lead's
 * `sourceLocale`, those reads return locale-*resolved* text in `title`/
 * `description` (source text falls back to a translation, or vice versa).
 * `EditLeadDialog` must seed its source-tab fields from the actual source
 * row, never from a resolved read — otherwise saving a translation can
 * silently overwrite the source columns with the resolved (translated)
 * text it was seeded with. This hook exists solely for that dialog.
 */
export function useGetLeadSource(entityId: string) {
  return useQuery({
    queryKey: [...leadKeys.byIdPrefix(entityId), 'source'] as const,
    queryFn: async (): Promise<Lead | null> => {
      const response = await apiClient.get<Lead>(`${LEAD_API_BASE}/${entityId}`)
      return response.data
    },
    enabled: !!entityId,
  })
}

export function useGetLeadsByInvestigation(investigationId: string) {
  const { i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  return useQuery({
    queryKey: leadKeys.byInvestigation(investigationId, locale),
    queryFn: async (): Promise<Lead[]> => {
      const response = await apiClient.get<Lead[]>(
        `${LEAD_API_BASE}/by-investigation/${investigationId}`,
        { params: { lang: locale } },
      )
      return response.data
    },
    enabled: !!investigationId,
    staleTime: 1000 * 60 * 1,
  })
}

export function useGetLeadsBySubmitter(username: string) {
  return useQuery({
    queryKey: leadKeys.bySubmitter(username),
    queryFn: async (): Promise<Lead[]> => {
      const response = await apiClient.get<Lead[]>(
        `${LEAD_API_BASE}/by-submitter/${encodeURIComponent(username)}`,
      )
      return response.data
    },
    enabled: !!username,
    staleTime: 1000 * 60 * 1,
  })
}

export function useGetLeadsGroupedByInvestigation() {
  return useQuery({
    queryKey: leadKeys.grouped(),
    queryFn: async (): Promise<Record<string, Lead[]>> => {
      const response = await apiClient.get<Record<string, Lead[]>>(
        `${LEAD_API_BASE}/grouped-by-investigation`,
      )
      return response.data
    },
    staleTime: 1000 * 60 * 1,
  })
}

export function useGetMyLeads() {
  return useQuery({
    queryKey: leadKeys.myLeads(),
    queryFn: async (): Promise<{ leads: Lead[]; count: number }> => {
      const response = await apiClient.get<{ leads: Lead[]; count: number }>(
        `${LEAD_API_BASE}/my-leads`,
      )
      return response.data
    },
  })
}

export function useGetUserLeads(username: string) {
  return useQuery({
    queryKey: leadKeys.userLeads(username),
    queryFn: async (): Promise<{ leads: Lead[]; count: number }> => {
      const response = await apiClient.get<{ leads: Lead[]; count: number }>(
        `${LEAD_API_BASE}/user/${username}`,
      )
      return response.data
    },
    enabled: !!username,
  })
}

export function useSearchLeads(filters: LeadFilters, limit: number = 20, offset: number = 0) {
  return useQuery({
    queryKey: leadKeys.search(filters),
    queryFn: async (): Promise<{ leads: Lead[]; total: number }> => {
      const response = await apiClient.get<{ leads: Lead[]; total: number }>(LEAD_API_BASE, {
        params: { ...filters, limit, offset },
      })
      return response.data
    },
    staleTime: 1000 * 60 * 1,
  })
}

export function useCreateLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateLeadFormData): Promise<Lead> => {
      const response = await apiClient.post<Lead>(LEAD_API_BASE, data)
      return response.data
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all })
      queryClient.invalidateQueries({ queryKey: leadKeys.grouped() })
      queryClient.invalidateQueries({ queryKey: leadKeys.myLeads() })
      queryClient.invalidateQueries({
        queryKey: leadKeys.byInvestigationPrefix(variables.investigationId),
      })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useUpdateLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & UpdateLeadFormData): Promise<Lead> => {
      const response = await apiClient.patch<Lead>(`${LEAD_API_BASE}/${id}`, data)
      return response.data
    },
    // Awaited so `mutateAsync` doesn't resolve — and the edit dialog
    // doesn't close, ready to be reopened — until the invalidated queries
    // have actually refetched.
    //
    // Also invalidates `byInvestigationPrefix` (from the mutation *result*,
    // not the variables — `UpdateLeadFormData` never carries
    // `investigationId`) — this is the query the management investigation
    // detail page's lead list (LeadsSection -> LeadTile -> EditLeadDialog)
    // actually reads its `lead` prop from. It was missing entirely before:
    // only `useCreateLead` touched it, and only because it has
    // `investigationId` in its own mutation variables.
    //
    // `EditLeadDialog`'s OWN data source (`useGetLeadSource`,
    // `useGetEntityTranslationState`) is refetched by the dialog itself
    // (see `EditLeadDialog.handleSubmit`) rather than patched from here —
    // simpler and race-free: TanStack Query's `refetch()` supersedes
    // whatever fetch for that query was already in flight, so there's no
    // need to manually cancel/abort a stale mount-time request racing this
    // mutation's own cache writes.
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: leadKeys.all }),
        queryClient.invalidateQueries({ queryKey: leadKeys.byIdPrefix(result.id) }),
        queryClient.invalidateQueries({ queryKey: leadKeys.grouped() }),
        queryClient.invalidateQueries({ queryKey: leadKeys.myLeads() }),
        queryClient.invalidateQueries({
          queryKey: leadKeys.byInvestigationPrefix(result.investigationId),
        }),
        // Refreshes the translation dashboard and its drill-down pages —
        // this lead's own completeness (and every ancestor's cascaded
        // aggregate) may have just changed.
        queryClient.invalidateQueries({ queryKey: ['translations'] }),
      ])
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useSubmitLeadFromChat() {
  return useMutation({
    mutationFn: async (data: {
      investigation: string
      description: string
      submitterUsername: string
    }): Promise<{ success: boolean; message: string; lead?: Lead }> => {
      const response = await apiClient.post(`${LEAD_API_BASE}/submit`, data)
      return response.data
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useSetLeadConclusion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      conclusion,
    }: {
      id: string
      conclusion: 'true' | 'false' | 'fallacious' | 'completed' | null
    }): Promise<Lead> => {
      const response = await apiClient.patch<Lead>(`${LEAD_API_BASE}/${id}/conclusion`, {
        conclusion,
      })
      return response.data
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.byIdPrefix(result.id) })
      queryClient.invalidateQueries({
        queryKey: leadKeys.byInvestigationPrefix(result.investigationId),
      })
      queryClient.invalidateQueries({ queryKey: leadKeys.all })
      queryClient.invalidateQueries({ queryKey: leadKeys.grouped() })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useUpdateLeadNotes() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id: entityId,
      notes,
    }: {
      id: string
      notes: string
    }): Promise<{ notes: string | null }> => {
      const response = await apiClient.patch<{ notes: string | null }>(
        `${LEAD_API_BASE}/${entityId}/notes`,
        { notes },
      )
      return response.data
    },
    onSuccess: (_result, { id: entityId }) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.byIdPrefix(entityId) })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useDeleteLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (entityId: string): Promise<{ success: boolean }> => {
      const response = await apiClient.delete<{ success: boolean }>(`${LEAD_API_BASE}/${entityId}`)
      return response.data
    },
    onSuccess: (_result, entityId) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all })
      queryClient.invalidateQueries({ queryKey: leadKeys.byIdPrefix(entityId) })
      queryClient.invalidateQueries({ queryKey: leadKeys.grouped() })
      queryClient.invalidateQueries({ queryKey: leadKeys.myLeads() })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function usePublishLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (leadId: string): Promise<Lead> => {
      const response = await apiClient.patch<Lead>(`${LEAD_API_BASE}/${leadId}/publish`)
      return response.data
    },
    onSuccess: (result, leadId) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.byIdPrefix(leadId) })
      queryClient.invalidateQueries({ queryKey: leadKeys.all })
      queryClient.invalidateQueries({ queryKey: leadKeys.grouped() })
      queryClient.invalidateQueries({
        queryKey: leadKeys.byInvestigationPrefix(result.investigationId),
      })
      queryClient.invalidateQueries({
        queryKey: investigationKeys.byIdPrefix(result.investigationId),
      })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useUnpublishLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (leadId: string): Promise<Lead> => {
      const response = await apiClient.patch<Lead>(`${LEAD_API_BASE}/${leadId}/unpublish`)
      return response.data
    },
    onSuccess: (result, leadId) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.byIdPrefix(leadId) })
      queryClient.invalidateQueries({ queryKey: leadKeys.all })
      queryClient.invalidateQueries({ queryKey: leadKeys.grouped() })
      queryClient.invalidateQueries({
        queryKey: leadKeys.byInvestigationPrefix(result.investigationId),
      })
      queryClient.invalidateQueries({
        queryKey: investigationKeys.byIdPrefix(result.investigationId),
      })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useReorderLeads(investigationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (leadIds: string[]): Promise<Lead[]> => {
      const response = await apiClient.patch<Lead[]>(
        `/investigations/${investigationId}/leads/reorder`,
        { leadIds },
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.byInvestigationPrefix(investigationId) })
      queryClient.invalidateQueries({ queryKey: leadKeys.all })
      queryClient.invalidateQueries({ queryKey: leadKeys.grouped() })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useGetLeadGroups(): {
  data?: LeadGroup[]
  isLoading: boolean
  error?: Error
  refetch: () => void
} {
  const { data, isLoading, error, refetch } = useGetLeadsGroupedByInvestigation()
  const { data: investigations } = useGetAllInvestigations()

  const groups: LeadGroup[] = []

  if (data && investigations) {
    Object.entries(data).forEach(([investigationId, leads]) => {
      const investigation = investigations.find(
        (investigation) => investigation.id === investigationId,
      )
      if (investigation) {
        groups.push({
          investigation,
          leads: leads.sort(
            (itemA, itemB) =>
              new Date(itemA.createdAt).getTime() - new Date(itemB.createdAt).getTime(),
          ),
        })
      }
    })
    groups.sort((itemA, itemB) => itemA.investigation.name.localeCompare(itemB.investigation.name))
  }

  return {
    data: groups,
    isLoading,
    error: error ?? undefined,
    refetch,
  }
}
